import { coachOffersKeys } from "@/modules/coachOffers/gateway/coachOfferKeys"
import { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { CoachOfferPriceEntity } from "@modules/coachOffers/entities/coachOfferPriceEntity"
import {
  type CoachOfferCreationInput,
  type CoachOfferUpdateInput,
  type PaymentOfferCreationInput,
} from "@modules/coachOffers/gateway/coachOffersDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export function useCoachOffers() {
  const { coachOffersApi } = useServicesContext()

  const { data } = useSuspenseQuery<CoachOfferEntity[]>({
    queryKey: coachOffersKeys.list(),
    queryFn: async () => {
      const result = await coachOffersApi.getCoachOffers()
      return result.map((offer) => CoachOfferEntity.fromDto(offer))
    },
  })

  return {
    data,
  }
}

export function useCoachOffer(id: string | undefined) {
  const { coachOffersApi } = useServicesContext()

  const { data } = useSuspenseQuery({
    queryKey: coachOffersKeys.detail(id ?? ""),
    queryFn: async (): Promise<CoachOfferEntity | null> => {
      if (!id) return null
      const result = await coachOffersApi.getCoachOffer(id)
      return CoachOfferEntity.fromDto(result)
    },
  })

  return {
    data,
  }
}

export function useCreateCoachOffer() {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const createCoachOfferMutation = useMutation({
    mutationFn: async (input: CoachOfferCreationInput) => {
      const result = await coachOffersApi.createCoachOffer(input)
      return CoachOfferEntity.fromDto(result)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
    },
  })

  return createCoachOfferMutation
}

export function useUpdateCoachOffer(id: string) {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const updateCoachOfferMutation = useMutation({
    mutationFn: async (input: CoachOfferUpdateInput) => {
      const result = await coachOffersApi.updateCoachOffer(id, input)
      return CoachOfferEntity.fromDto(result)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.detail(id) })
    },
  })

  return updateCoachOfferMutation
}

export function useDeleteCoachOffer() {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const deleteCoachOfferMutation = useMutation({
    mutationFn: async (id: string) => {
      await coachOffersApi.deleteCoachOffer(id)
    },
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
      queryClient.removeQueries({ queryKey: coachOffersKeys.detail(id) })
    },
  })

  return deleteCoachOfferMutation
}

export function useCreatePaymentOffer(offerId: string) {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const createPaymentOfferMutation = useMutation({
    mutationFn: async (input: PaymentOfferCreationInput) => {
      const result = await coachOffersApi.createPaymentOffer(offerId, input)
      return CoachOfferPriceEntity.fromDto(result)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.detail(offerId) })
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
    },
  })

  return createPaymentOfferMutation
}

export function useUpdatePaymentOffer(offerId: string) {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const updatePaymentOfferMutation = useMutation({
    mutationFn: async ({ paymentOfferId, input }: { paymentOfferId: string; input: PaymentOfferCreationInput }) => {
      const result = await coachOffersApi.updateCoachOfferPaymentOffer(offerId, paymentOfferId, input)
      return CoachOfferPriceEntity.fromDto(result)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.detail(offerId) })
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
    },
  })

  return updatePaymentOfferMutation
}

export function useUpdateCoachOffersOrder() {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: saveOrder, isPending } = useMutation({
    mutationFn: async (visibleOrder: string[]) => {
      const orderMap: Record<string, number> = {}
      let position = 1
      visibleOrder.forEach((id) => {
        orderMap[id] = position++
      })
      await coachOffersApi.arrangeOffersDisplayOrder(orderMap)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
    },
  })

  return { saveOrder, isPending }
}

export function useDeletePaymentOffer(offerId: string) {
  const { coachOffersApi } = useServicesContext()
  const queryClient = useQueryClient()

  const deletePaymentOfferMutation = useMutation({
    mutationFn: async (paymentOfferId: string) => {
      await coachOffersApi.deleteCoachOfferPaymentOffer(offerId, paymentOfferId)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.detail(offerId) })
      await queryClient.invalidateQueries({ queryKey: coachOffersKeys.list() })
    },
  })

  return deletePaymentOfferMutation
}
